import React, { useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import type { VersionChanges } from '@/utils/changelogParser';

interface WhatsNewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: VersionChanges | null;
}

export const WhatsNewModal: React.FC<WhatsNewModalProps> = ({
  open,
  onOpenChange,
  changes,
}) => {
  const languageContext = useContext(LanguageProviderContext);
  
  // If there are no changes or the language context is not available, return null
  if (!changes || !languageContext) return null;

  const { translator } = languageContext;

  const sections = [
    {
      key: 'added',
      title: translator('WhatsNew.added'),
      icon: Plus,
      color: 'text-green-500',
      items: changes.added,
    },
    {
      key: 'changed',
      title: translator('WhatsNew.changed'),
      icon: AlertCircle,
      color: 'text-blue-500',
      items: changes.changed,
    },
    {
      key: 'fixed',
      title: translator('WhatsNew.fixed'),
      icon: CheckCircle,
      color: 'text-orange-500',
      items: changes.fixed,
    },
    {
      key: 'removed',
      title: translator('WhatsNew.removed'),
      icon: Trash2,
      color: 'text-red-500',
      items: changes.removed,
    },
  ].filter(section => section.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {translator('WhatsNew.titleVersion').replace('{version}', changes.version)}
          </DialogTitle>
          {changes.date && (
            <p className="text-sm text-muted-foreground">{changes.date}</p>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="space-y-6">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div key={section.key}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`h-5 w-5 ${section.color}`} />
                    <h3 className="font-semibold text-lg">{section.title}</h3>
                  </div>
                  <ul className="space-y-2 ml-7">
                    {section.items.map((item, index) => (
                      <li key={index} className="text-sm text-muted-foreground leading-relaxed">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            {translator('WhatsNew.closeButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
